import { post } from '../../lib/api/client';

/**
 * Recall — "who is affected" (BE `POST /admin/recall/query`,
 * admin-content-feedback.go). Exactly one scope: a dish, a listing, a chef, or
 * an ingredient / tag (matched against allergen and origin tags and the free
 * ingredients text), or a dish / chef name (substring); optionally narrowed to
 * locales. `from` / `to` are INCLUSIVE days in the admin's profile
 * timezone. Canceled, abandoned, expired and held orders are excluded — no food
 * reached anyone. Notify and the recall log are not built (501).
 */

/** dishName / chefName match a case-insensitive substring of the listing title / chef name. */
export type RecallBy = 'dishId' | 'listingId' | 'chefProfileId' | 'ingredientTag' | 'dishName' | 'chefName';

export interface RecallParams {
  by: RecallBy;
  value: string;
  from?: string;
  to?: string;
  /** Narrows the scope to listings in these locales. */
  localeIds?: string[];
}

export interface RecallAffectedOrder {
  orderId: string;
  orderNumber: string;
  status: string;
  quantity: number;
  orderedAt: string;
  listingId: string;
  dishTitle: string;
  chefProfileId: string;
  chefName: string;
  eaterProfileId: string;
  eaterName: string;
  eaterEmail?: string;
  localeId?: string;
}

export interface RecallResult {
  affectedOrders: RecallAffectedOrder[];
  orderCount: number;
  portionCount: number;
  eaterProfileIds: string[];
  eaterCount: number;
  chefCount: number;
}

export interface RecallResponse {
  result: RecallResult;
  /** Operational warnings, e.g. more affected eaters than a notify call accepts. */
  warnings: string[];
}

export async function runRecallQuery({ by, value, from, to, localeIds }: RecallParams): Promise<RecallResponse> {
  const res = await post<RecallResponse>('/admin/recall/query', {
    [by]: value.trim(),
    from,
    to,
    localeIds: localeIds?.length ? localeIds : undefined,
  });
  return {
    warnings: res.warnings ?? [],
    result: {
      ...res.result,
      affectedOrders: res.result.affectedOrders ?? [],
      eaterProfileIds: res.result.eaterProfileIds ?? [],
    },
  };
}

/** One CSV row per affected eater: who to contact and how much they had. */
export function affectedEatersCsv(orders: RecallAffectedOrder[]): string {
  const byEater = new Map<string, { name: string; email: string; orders: Set<string>; portions: number }>();
  for (const o of orders) {
    const e = byEater.get(o.eaterProfileId) ?? {
      name: o.eaterName,
      email: o.eaterEmail ?? '',
      orders: new Set(),
      portions: 0,
    };
    e.orders.add(o.orderNumber || o.orderId);
    e.portions += o.quantity;
    byEater.set(o.eaterProfileId, e);
  }
  const cell = (v: string | number) => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [['eater_profile_id', 'name', 'email', 'orders', 'portions', 'order_numbers'].join(',')];
  for (const [id, e] of byEater) {
    lines.push([id, e.name, e.email, e.orders.size, e.portions, [...e.orders].join(' ')].map(cell).join(','));
  }
  return lines.join('\n') + '\n';
}
